/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SalesOrdersHeader } from '../../interfaces/salesOrders.model';
import { SalesBudgetsHeader } from '../../interfaces/sales-budgets.model';
import { Functions } from '../../functions/functions';

@Injectable({
  providedIn: 'root'
})
export abstract class BaseResourceOnlineService {
  protected http: HttpClient;
  protected url: string;
  protected functions = inject(Functions);

  constructor(protected injector: Injector, protected apiPath: string) {
    this.http = injector.get(HttpClient);
    this.url = `${environment.url}api/tgv/${this.apiPath}`;
  }

  get(
    page = 1,
    pageSize = 10,
    filter = '',
    fields = '',
    sort = ''
  ): Observable<any> {
    const parameters = new HttpParams()
      .append('page', page.toString())
      .append('pageSize', pageSize.toString())
      .append('FILTER', filter)
      .append('FIELDS', fields)
      .append('SORT', sort);

    return this.http.get(this.url, { params: parameters });
  }

  getCustom(
    url: string,
    page = 1,
    pageSize = 10,
    filter = '',
    fields = '',
    sort = '',
    queryString?: string[]
  ): Observable<any> {
    let parameters = new HttpParams()
      .append('page', page.toString())
      .append('pageSize', pageSize.toString())
      .append('FILTER', filter)
      .append('FIELDS', fields)
      .append('SORT', sort)

    if (queryString?.length > 0){
      parameters = parameters.append(queryString[0], queryString[1]);
    }

    return this.http.get(url, { params: parameters });
  }

  getCustomSuffix(
    suffixUrl: string,
    page = 1,
    pageSize = 10,
    filter = '',
    fields = '',
    sort = ''
  ): Observable<any> {
    const newFilter = filter ? this.fixedEncodeURIComponent(filter) : '';
    return this.http.get(`${this.url}${suffixUrl}/?page=${page}&pageSize=${pageSize}&FILTER=${newFilter}&FIELDS=${fields}&SORT=${sort}`);
  }

  fixedEncodeURIComponent(str: string): string {
    return encodeURIComponent(str).replace(str,this.changeCharToUriCode(str));
   }

  changeCharToUriCode(c: string): string{
    return '%' + c.charCodeAt(0).toString(16);
  }

  getById(id: string): Observable<any> {
    return this.http.get(`${this.url}/${id}`);
  }

  create(body: any): Observable<any> {
    return this.http.post(this.url, body);
  }

  update(id: string, body: any): Observable<any> {
    const url = `${this.url}/${id}`;
    return this.http.put(url, body);
  }

  delete(id: string): Observable<any> {
    const url = `${this.url}/${id}`;
    return this.http.delete(url);
  }

  //#region auxiliares
  buildFilter(filter: any): string {
    if (filter.inputSearch !== undefined) {
      return this.buildFilterContainsOr(filter);
    } else {
      return this.buildFilterAnd(filter);
    }
  }

  buildFilterContainsOr(filter: any): string {
    let queryParam = '';

    if (filter.fields) {
      filter.fields.map((atr, index) => {
        if (index === 0) {
          queryParam += filter.additionalFilter ? '(' : '';
          queryParam += `contains(${atr}, '${this.getValidMaskInput(atr,filter.inputSearch)}')`;
        } else {
          queryParam += ` or contains(${atr}, '${this.getValidMaskInput(atr,filter.inputSearch)}')`;
        }
      });
    }

    if (filter.additionalFilter) {
      queryParam += queryParam
        ? `) and ${filter.additionalFilter}`
        : filter.additionalFilter;
    }

    return queryParam;
  }

  getValidMaskInput(field: string, filter: string): string{
    return field === 'cgc' ?
      // eslint-disable-next-line no-useless-escape
      filter.replace(/[./-]/g, '')
      : filter;
  }

  buildFilterContainsAnd(filter: any): string {
    let queryParam = '';

    filter.fields.map((atr, index) => {
      if (index === 0) {
        queryParam += `contains(${atr}, '${filter.inputSearch}')`;
      } else {
        queryParam += ` and contains(${atr}, '${filter.inputSearch}')`;
      }
    });

    return queryParam;
  }

  buildFilterAnd(filter: any): string {
    return this.buildFilterEqual(filter).join('and ');
  }

  buildFilterOr(filter: any): string {
    return this.buildFilterEqual(filter).join('or ');
  }

  buildFilterEqual(filter): Array<any> {
    return Object.keys(filter).map(atr => `${atr} eq '${filter[atr]}'`);
  }

  hasFilter(filter: any): boolean {
    return filter !== undefined;
  }

  filterContainsOr(indexParam: number, filter: any, attribute: string): {index: number; queryParam: string} {
    let query = '';

    if (indexParam === 0) {
      query += `contains(${attribute}, '${filter[attribute]}')`;
    } else {
      query += ` or contains(${attribute}, '${filter[attribute]}')`;
    }

    indexParam++;
    return { index: indexParam, queryParam: query };
  }

  filterEqualAnd(indexParam: number, filter: any, attribute: string): {index: number; queryParam: string} {
    let query = '';

    if (indexParam === 0) {
      query += `${attribute} eq '${filter[attribute]}'`;
    } else {
      query += ` and ${attribute} eq '${filter[attribute]}'`;
    }

    indexParam++;
    return { index: indexParam, queryParam: query };
  }
  //#endregion
  //#regionFilterSeller
  buildFilterRequest(filter = []): string{
    let queryParam = '';

    filter.forEach((filter, index, rules )=>{
      queryParam += '(';

      filter.rules.forEach((rule, index, rules) =>{
        queryParam += this.parseOperatorFilterOData(rule.attribute, rule.operator, rule.value);
        if(index < rules.length-1){
          queryParam += ` ${filter.logicalOperator} `;
        }
      });

      queryParam += ')';

      if(index < rules.length-1){
        queryParam += ' and ';
      }
    });
    return queryParam;
  }

  parseOperatorFilterOData(attribute: string, operator: string, value: string): string{
    let filter: string;
    if(operator == 'contains'){
      filter = `${operator}(${attribute}, '${value}')`;
    }else{
      filter = `${attribute} ${operator} '${value}'`;
    }

    return filter;
  }

  getSyncedRecords(page = 1,
    pageSize = 10,
    filter = '',
    fields = '',
    sort = ''): Observable<any[]> {
    const url = `${this.url}/sync`;
    return this.getCustom(url, page, pageSize, filter, fields, sort);
  }

  treatsRulesOfDiscounts<T>(items: T[], isDiscountCustomer: boolean = false): T[] {
    if ( this.functions.compareVersions(localStorage.getItem('serverVersion'), '1.4.7') > 0) {
      items.forEach((i, index) => {
        if ( !i['pricelist'] || i['digitedUnitPrice'] >= 0) {
          delete items[index]['pricelist'];
        }
        if (!i['digitedUnitPrice'] && i['pricelist'] && !i['orderid'] && !i['budgetid']) {
          delete items[index]['unitprice'];
        }
        if (isDiscountCustomer && !i['digitedUnitPrice']) {
          delete items[index]['unitprice'];
          delete items[index]['amount'];
        }
      });
    }
    return items;
  }

  treatsRulesOfDiscountsHeader(salesHeader: SalesOrdersHeader | SalesBudgetsHeader, isDiscountCustomer: boolean): SalesOrdersHeader | SalesBudgetsHeader  {
    if (isDiscountCustomer){
      delete salesHeader['discount'];
    }
    return salesHeader;
  }

  updateCustom( body, ...params: string[]): Observable<any> {
    return this.http.put(`${this.url}/${params.join('/')}`, body);
  }

  deleteCustom(...params: string[]): Observable<any> {
    return this.http.delete(`${this.url}/${params.join('/')}`);
  }

  getByIdCustom(...params: string[]): Observable<any> {
    return this.http.get(`${this.url}/${params.join('/')}`);
  }

  isDiscountCustomer(discount: number): boolean {
    if (discount && discount > 0) {
      return true;
    }
    return false;
  }
}
